import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Courrier } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface CourrierState {
  courriers: Courrier[];
  courrier: Courrier | null;
  loading: boolean;
  error: string | null;
}

const initialState: CourrierState = {
  courriers: [],
  courrier: null,
  loading: false,
  error: null,
};

// ✅ Fonction utilitaire pour normaliser les réponses API
const normalizeCourriersResponse = (responseData: any): Courrier[] => {
  // Si c'est déjà un tableau, le retourner
  if (Array.isArray(responseData)) {
    return responseData;
  }
  
  // Si c'est un objet avec une propriété 'data'
  if (responseData?.data && Array.isArray(responseData.data)) {
    return responseData.data;
  }
  
  // Si c'est un objet avec 'success' et 'data'
  if (responseData?.success && responseData?.data && Array.isArray(responseData.data)) {
    return responseData.data;
  }
  
  // Dans tous les autres cas, retourner un tableau vide
  console.warn('⚠️ Format de réponse inattendu pour courriers:', responseData);
  return [];
};

export const fetchCourriers = createAsyncThunk<Courrier[], void, { state: RootState }>(
  'courrier/fetchCourriers',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();

      console.log('🔍 Récupération des courriers...');
      
      const response = await axios.get(`${baseUrlApi}/courriers`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });

      console.log('📦 Réponse API courriers:', response.data);
      
      // ✅ Normaliser la réponse pour garantir un tableau
      const courriers = normalizeCourriersResponse(response.data);
      
      console.log(`✅ ${courriers.length} courrier(s) récupéré(s)`);
      
      return courriers;
      
    } catch (error: any) {
      console.error('❌ Erreur fetchCourriers:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch courriers');
    }
  }
);

// ✅ CORRECTION: Les chevrons doivent être sur une seule ligne
export const fetchCourrier = createAsyncThunk<
  Courrier,
  { LocalId: number },
  { state: RootState }
>(
  'courrier/fetchCourrier',
  async ({ LocalId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      
      console.log('🔍 Récupération courrier pour local:', LocalId);
      
      const response = await axios.get(
        `${baseUrlApi}/courriers/local/${LocalId}`,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` }
        }
      );
      
      console.log('✅ Courrier récupéré:', response.data);
      dispatch(fetchCourriers());
      
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Erreur fetchCourrier:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch courrier');
    }
  }
);

// ✅ createCourrier (sans 's') - Version standard
export const createCourrier = createAsyncThunk<Courrier, Partial<Courrier>, { state: RootState }>(
  'courrier/createCourrier',
  async (courrierData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      
      console.log('📝 Création courrier...');
      
      const response = await axios.post(`${baseUrlApi}/courriers`, courrierData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      
      console.log('✅ Courrier créé:', response.data);
      dispatch(fetchCourriers());
      
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Erreur createCourrier:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create courrier');
    }
  }
);

// ✅ createCourriers (avec 's') - Pour compatibilité avec l'ancienne version
export const createCourriers = createAsyncThunk<Courrier, Partial<Courrier>, { state: RootState }>(
  'courrier/createCourriers',
  async (courrierData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      
      console.log('📝 Création courrier (createCourriers)...');
      
      const response = await axios.post(`${baseUrlApi}/courriers`, courrierData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      
      console.log('✅ Courrier créé avec succès');
      dispatch(fetchCourriers());
      
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Erreur createCourriers:', error);
      return rejectWithValue(error.response?.data?.message || 'La génération du courrier a échoué !');
    }
  }
);

export const updateCourrier = createAsyncThunk<
  Courrier,
  { courrierData: Partial<Courrier>; courrierId: number },
  { state: RootState }
>(
  'courrier/updateCourrier',
  async ({ courrierData, courrierId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      
      console.log('🔄 Mise à jour courrier:', courrierId);
      
      const response = await axios.put(
        `${baseUrlApi}/courriers/${courrierId}`,
        courrierData,
        {
          headers: { Authorization: `bearer ${authentification.user.token}` }
        }
      );
      
      console.log('✅ Courrier mis à jour');
      dispatch(fetchCourriers());
      
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Erreur updateCourrier:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update courrier');
    }
  }
);

export const deleteCourrier = createAsyncThunk<number, number, { state: RootState }>(
  'courrier/deleteCourrier',
  async (courrierId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      
      console.log('🗑️ Suppression courrier:', courrierId);
      
      await axios.delete(`${baseUrlApi}/courriers/${courrierId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      
      console.log('✅ Courrier supprimé');
      dispatch(fetchCourriers());
      
      return courrierId;
      
    } catch (error: any) {
      console.error('❌ Erreur deleteCourrier:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to delete courrier');
    }
  }
);

const courrierSlice = createSlice({
  name: 'courrier',
  initialState,
  reducers: {
    clearCourrier: (state) => { 
      state.courrier = null; 
    },
    clearCourriers: (state) => { 
      state.courriers = []; 
    },
    resetCourrierState: () => initialState,
  },
  extraReducers: (builder) => {
    // Gestion des états de chargement pour tous les thunks
    [
      fetchCourriers, 
      fetchCourrier, 
      createCourrier, 
      createCourriers,
      updateCourrier, 
      deleteCourrier
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
          // ✅ CRITIQUE: En cas d'erreur, garder un tableau vide
          if (thunk === fetchCourriers) {
            state.courriers = [];
          }
        });
    });

    // Cas fulfilled spécifiques
    builder
      .addCase(fetchCourriers.fulfilled, (state, action) => {
        state.loading = false;
        // ✅ PROTECTION: S'assurer que c'est toujours un tableau
        state.courriers = Array.isArray(action.payload) ? action.payload : [];
        console.log('📦 Courriers stockés dans Redux:', state.courriers.length);
      })
      .addCase(fetchCourrier.fulfilled, (state, action) => {
        state.loading = false;
        state.courrier = action.payload;
      })
      .addCase(createCourrier.fulfilled, (state) => {
        state.loading = false;
        // ✅ Ne pas ajouter ici car fetchCourriers est appelé automatiquement
      })
      .addCase(createCourriers.fulfilled, (state) => {
        state.loading = false;
        // ✅ Ne pas ajouter ici car fetchCourriers est appelé automatiquement
      })
      .addCase(updateCourrier.fulfilled, (state) => {
        state.loading = false;
        // ✅ Ne pas modifier ici car fetchCourriers est appelé automatiquement
      })
      .addCase(deleteCourrier.fulfilled, (state) => {
        state.loading = false;
        // ✅ Ne pas filtrer ici car fetchCourriers est appelé automatiquement
      });
  },
});

export const { clearCourrier, clearCourriers, resetCourrierState } = courrierSlice.actions;
export default courrierSlice.reducer;